import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type MetadataCategoryView,
  type MetadataPackageDetail,
  type MetadataPackageSummary,
  type MetadataPackageVersionInput,
  type MetadataPackageVersionView,
  type MetadataSuggestionView
} from "../../api/client";
import { useAuth } from "../../app/auth/AuthProvider";
import { ConfirmDialog } from "../../components/feedback/ConfirmDialog";
import { EmptyState, ErrorState, LoadingState } from "../../components/feedback/AsyncState";
import { formatProblem, isApiProblem } from "../../api/problemDetails";

const etagFor = (version: number | string): string => `"${version}"`;

interface MetadataDraft {
  sourceSuggestionId: string | null;
  title: string;
  description: string;
  tags: string;
  categoryId: string;
  language: string;
  playlistReference: string;
}

type MetadataSource = Pick<MetadataPackageVersionView, "sourceSuggestionId" | "title" | "description" | "tags" | "categoryId" | "language" | "playlistReference">
  | Pick<MetadataSuggestionView, "id" | "title" | "description" | "tags" | "categoryId" | "language" | "playlistReference">;

function draftFrom(source: MetadataSource): MetadataDraft {
  return {
    sourceSuggestionId: "sourceSuggestionId" in source ? source.sourceSuggestionId : source.id,
    title: source.title,
    description: source.description,
    tags: source.tags.join(", "),
    categoryId: source.categoryId ?? "",
    language: source.language ?? "en",
    playlistReference: source.playlistReference ?? ""
  };
}

function emptyDraft(): MetadataDraft {
  return { sourceSuggestionId: null, title: "", description: "", tags: "", categoryId: "", language: "en", playlistReference: "" };
}

function tagsFrom(value: string): string[] {
  return [...new Set(value.split(",").map(tag => tag.trim()).filter(Boolean))];
}

function shortId(value: string | null | undefined): string {
  return value ? value.slice(0, 8) : "Not pinned";
}

function timestamp(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function metadataMutationErrorMessage(error: unknown): string {
  if (!isApiProblem(error)) {
    return "The metadata command could not be completed. Refresh the current package and try again.";
  }

  const message = error.code === "precondition_failed"
    ? "The campaign changed while this metadata revision was open. Refresh the package, stage the suggestion again, then save."
    : error.code === "metadata_output_not_ready"
      ? "This output is no longer ready for metadata selection. Choose an output marked ReadyToPublish, then refresh this page."
      : error.code === "metadata_suggestion_not_found" || error.code === "metadata_suggestion_unavailable"
        ? "The staged suggestion is no longer current. Refresh categories and suggestions, then choose it again."
        : error.code === "youtube_category_catalog_unavailable" || error.code === "youtube_category_catalog_stale"
          ? "A fresh YouTube category catalog is required. Refresh categories and suggestions, then wait for the generation to complete."
          : error.code === "youtube_metadata_category_invalid"
            ? "The selected YouTube category is no longer assignable. Select a current category and save again."
            : error.code === "youtube_metadata_title_invalid"
              ? "The title exceeds the YouTube limit or contains an unsupported character."
              : error.code === "youtube_metadata_description_invalid"
                ? "The rendered description exceeds the YouTube limit or contains an unsupported character. Shorten the editor description and save again."
                : error.code === "youtube_metadata_tags_invalid" || error.code === "youtube_metadata_tags_too_long"
                  ? "The tags contain an invalid value or exceed YouTube's combined tag limit."
                  : error.code === "youtube_metadata_language_invalid"
                    ? "Enter a valid language tag such as en or tr-TR."
                    : formatProblem(error);

  return error.code ? `${message} (${error.code})` : message;
}

/** Safe operator surface for selecting immutable YouTube metadata revisions. */
export function MetadataPackagePage({ outputId }: { outputId: string }): React.JSX.Element {
  const { can } = useAuth();
  const client = useQueryClient();
  const packages = useQuery({
    queryKey: ["metadata-packages", outputId],
    queryFn: () => api.listOutputMetadataPackages(outputId),
    retry: false,
    refetchInterval: 5_000
  });
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const packageId = selectedPackageId ?? packages.data?.data[0]?.id ?? "";
  const metadata = useQuery({
    queryKey: ["metadata-package", packageId],
    queryFn: () => api.getMetadataPackage(packageId),
    enabled: Boolean(packageId),
    retry: false,
    refetchInterval: packageId ? 5_000 : false
  });
  const detail = metadata.data?.data;
  const [draft, setDraft] = useState<MetadataDraft>(emptyDraft);
  const [loadedVersionId, setLoadedVersionId] = useState<string | null>(null);
  const [saveConfirm, setSaveConfirm] = useState(false);
  const [refreshSuggestionsConfirm, setRefreshSuggestionsConfirm] = useState(false);
  const [preflightConfirm, setPreflightConfirm] = useState(false);
  const [approvalConfirm, setApprovalConfirm] = useState(false);
  const [approvalReason, setApprovalReason] = useState("");
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);

  useEffect(() => {
    const current = detail?.currentVersion;
    if (current && current.id !== loadedVersionId) {
      setDraft(draftFrom(current));
      setLoadedVersionId(current.id);
      setApprovalReason("");
    }
  }, [detail?.currentVersion, loadedVersionId]);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["metadata-packages", outputId] }),
      packageId ? client.invalidateQueries({ queryKey: ["metadata-package", packageId] }) : Promise.resolve()
    ]);
  };
  const save = useMutation({
    mutationFn: () => {
      if (!detail) throw new Error("The current metadata package is unavailable.");
      const request: MetadataPackageVersionInput = {
        expectedCampaignVersion: detail.campaignVersion,
        title: draft.title.trim(),
        description: draft.description.trim(),
        tags: tagsFrom(draft.tags),
        categoryId: draft.categoryId,
        language: draft.language.trim(),
        playlistReference: draft.playlistReference.trim() || null,
        sourceSuggestionId: draft.sourceSuggestionId,
        confirm: true
      };
      return api.createMetadataPackage(outputId, request, etagFor(detail.campaignVersion));
    },
    onSuccess: async () => {
      setSaveConfirm(false);
      await refresh();
    },
    onError: () => setSaveConfirm(false)
  });
  const refreshSuggestions = useMutation({
    mutationFn: () => {
      if (!detail) throw new Error("The current metadata package is unavailable.");
      return api.refreshMetadataPackageSuggestions(outputId, {
        expectedCampaignVersion: detail.campaignVersion,
        confirm: true
      }, etagFor(detail.campaignVersion));
    },
    onSuccess: async () => {
      setRefreshSuggestionsConfirm(false);
      await refresh();
    },
    onError: () => setRefreshSuggestionsConfirm(false)
  });
  const preflight = useMutation({
    mutationFn: () => {
      if (!detail) throw new Error("The current metadata package is unavailable.");
      return api.preflightMetadataPackage(detail.id, { confirm: true }, etagFor(detail.version));
    },
    onSuccess: async () => {
      setPreflightConfirm(false);
      await refresh();
    },
    onError: () => setPreflightConfirm(false)
  });
  const approval = useMutation({
    mutationFn: () => {
      if (!detail) throw new Error("The current metadata package is unavailable.");
      return api.approveMetadataPackage(detail.id, { reason: approvalReason.trim(), confirm: true }, etagFor(detail.version));
    },
    onSuccess: async () => {
      setApprovalConfirm(false);
      await refresh();
    },
    onError: () => setApprovalConfirm(false)
  });

  if (packages.isLoading) return <LoadingState label="Loading publishing metadata…" />;
  if (packages.isError) return <ErrorState error={packages.error} retry={() => void packages.refetch()} />;
  if (!packages.data?.data.length) {
    return <section className="stack"><div className="page-heading"><div><p className="eyebrow">PUBLISHING METADATA</p><h1>Metadata package</h1><p>Automatic suggestions appear after the approved final render is available.</p></div></div><EmptyState title="Metadata suggestions are not ready" detail="The durable metadata job creates the package from the approved output, channel policy, and current read-only YouTube category catalog." action={<button type="button" className="button-secondary" onClick={() => void packages.refetch()}>Refresh metadata status</button>} /></section>;
  }
  if (metadata.isLoading) return <LoadingState label="Loading metadata package history…" />;
  if (metadata.isError) return <ErrorState error={metadata.error} retry={() => void metadata.refetch()} />;
  if (!detail) return <EmptyState title="Metadata package is unavailable" detail="Refresh the output after the metadata suggestion job completes." />;

  return <MetadataWorkspace
    outputId={outputId}
    packages={packages.data.data}
    detail={detail}
    selectedPackageId={packageId}
    onPackageChange={id => setSelectedPackageId(id)}
    editable={can("content.edit")}
    draft={draft}
    onDraftChange={setDraft}
    savePending={save.isPending}
    onSave={() => setSaveConfirm(true)}
    refreshSuggestionsPending={refreshSuggestions.isPending}
    onRefreshSuggestions={() => setRefreshSuggestionsConfirm(true)}
    preflightPending={preflight.isPending}
    onPreflight={() => setPreflightConfirm(true)}
    approvalPending={approval.isPending}
    onApprove={() => setApprovalConfirm(true)}
    approvalReason={approvalReason}
    onApprovalReasonChange={setApprovalReason}
    compareVersionId={compareVersionId}
    onCompareVersionChange={setCompareVersionId}
    mutationError={save.error ?? refreshSuggestions.error ?? preflight.error ?? approval.error}
    onRefresh={() => void refresh()}
    saveConfirm={saveConfirm}
    onSaveCancel={() => setSaveConfirm(false)}
    onSaveConfirm={() => save.mutate()}
    refreshSuggestionsConfirm={refreshSuggestionsConfirm}
    onRefreshSuggestionsCancel={() => setRefreshSuggestionsConfirm(false)}
    onRefreshSuggestionsConfirm={() => refreshSuggestions.mutate()}
    preflightConfirm={preflightConfirm}
    onPreflightCancel={() => setPreflightConfirm(false)}
    onPreflightConfirm={() => preflight.mutate()}
    approvalConfirm={approvalConfirm}
    onApprovalCancel={() => setApprovalConfirm(false)}
    onApprovalConfirm={() => approval.mutate()}
  />;
}

interface MetadataWorkspaceProps {
  outputId: string;
  packages: MetadataPackageSummary[];
  detail: MetadataPackageDetail;
  selectedPackageId: string;
  onPackageChange(id: string): void;
  editable: boolean;
  draft: MetadataDraft;
  onDraftChange(value: MetadataDraft): void;
  savePending: boolean;
  onSave(): void;
  refreshSuggestionsPending: boolean;
  onRefreshSuggestions(): void;
  preflightPending: boolean;
  onPreflight(): void;
  approvalPending: boolean;
  onApprove(): void;
  approvalReason: string;
  onApprovalReasonChange(value: string): void;
  compareVersionId: string | null;
  onCompareVersionChange(value: string): void;
  mutationError: unknown;
  onRefresh(): void;
  saveConfirm: boolean;
  onSaveCancel(): void;
  onSaveConfirm(): void;
  refreshSuggestionsConfirm: boolean;
  onRefreshSuggestionsCancel(): void;
  onRefreshSuggestionsConfirm(): void;
  preflightConfirm: boolean;
  onPreflightCancel(): void;
  onPreflightConfirm(): void;
  approvalConfirm: boolean;
  onApprovalCancel(): void;
  onApprovalConfirm(): void;
}

function MetadataWorkspace(props: MetadataWorkspaceProps): React.JSX.Element {
  const {
    outputId, packages, detail, selectedPackageId, onPackageChange, editable, draft, onDraftChange, savePending, onSave,
    refreshSuggestionsPending, onRefreshSuggestions, preflightPending, onPreflight, approvalPending, onApprove, approvalReason, onApprovalReasonChange, compareVersionId,
    onCompareVersionChange, mutationError, onRefresh, saveConfirm, onSaveCancel, onSaveConfirm, refreshSuggestionsConfirm,
    onRefreshSuggestionsCancel, onRefreshSuggestionsConfirm, preflightConfirm,
    onPreflightCancel, onPreflightConfirm, approvalConfirm, onApprovalCancel, onApprovalConfirm
  } = props;
  const current = detail.currentVersion;
  const catalog = detail.categoryCatalog;
  const catalogAvailable = catalog?.status === "Available" && catalog.isFresh;
  const categoryOptions = useMemo(() => mergeCategories(detail.categories, current), [detail.categories, current]);
  const currentPreflight = detail.currentPreflight?.packageVersionId === current?.id ? detail.currentPreflight : null;
  const currentPreflightFindings = currentPreflight
    ? detail.preflightFindings.filter(finding => finding.runId === currentPreflight.id)
    : [];
  const currentApproval = detail.approvals.find(approval => approval.packageVersionId === current?.id
    && approval.preflightRunId === currentPreflight?.id
    && (approval.decision === "Approved" || approval.decision === "ApprovedByPolicy")) ?? null;
  const currentPreflightAllowsApproval = currentPreflight?.status === "Passed";
  const gateStatus = metadataGateStatus(detail, currentPreflight?.status, currentApproval?.decision, catalogAvailable);
  const mutationMessage = mutationError ? metadataMutationErrorMessage(mutationError) : null;
  const canSave = editable && catalogAvailable && Boolean(draft.title.trim()) && Boolean(draft.description.trim()) && Boolean(draft.categoryId) && Boolean(draft.language.trim());
  const canPreflight = editable && Boolean(current) && catalogAvailable;
  const canApprove = editable && catalogAvailable && detail.requiresHumanApproval && currentPreflightAllowsApproval && !currentApproval && Boolean(approvalReason.trim());
  const history = [...detail.versions].sort((left, right) => right.number - left.number);
  const comparison = compareVersionId === ""
    ? null
    : history.find(version => version.id === compareVersionId)
      ?? (compareVersionId === null ? history.find(version => version.id !== current?.id) ?? null : null);

  return <section className="stack">
    <div className="page-heading"><div><p className="eyebrow">PUBLISHING METADATA</p><h1>Metadata package</h1><p>Private, immutable metadata for output {outputId.slice(0, 8)}. Saving a revision keeps its media pins but returns the output to ReadyToPublish until the current preflight and policy gate pass again.</p></div><div className="actions"><button type="button" className="button-secondary" onClick={onRefresh}>Refresh</button><span className={`status status-${gateStatus.toLowerCase().replaceAll(" ", "-")}`}>{gateStatus}</span></div></div>

    {packages.length > 1 && <label>Metadata package<select value={selectedPackageId} onChange={event => onPackageChange(event.target.value)}>{packages.map(item => <option key={item.id} value={item.id}>{item.currentVersionId ? `Selected revision ${shortId(item.currentVersionId)}` : "Suggestions pending"}</option>)}</select></label>}

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">LIVE CATEGORY CATALOG</p><h2>{catalog?.status ?? "Unavailable"}</h2><p>{catalog ? `${catalog.countryCode} YouTube category catalog` : "A connected YouTube category catalog has not been recorded."}</p></div><div className="actions"><span className={`status status-${catalog?.status.toLowerCase() ?? "failed"}`}>{detail.categories.length} category options</span>{editable && <button type="button" className="button-secondary" disabled={refreshSuggestionsPending} onClick={onRefreshSuggestions}>{refreshSuggestionsPending ? "Queueing refresh…" : "Refresh categories and suggestions"}</button>}</div></div>
      {catalogAvailable ? <p className="muted">Fetched {timestamp(catalog?.fetchedAt)} · valid until {timestamp(catalog?.expiresAt)}. Metadata preflight uses this read-only catalog.</p> : <p className="field-error">The category catalog is unavailable{catalog?.resultCode ? ` (${catalog.resultCode})` : ""}. Selecting, preflighting, and approving metadata stays blocked until a fresh catalog is available.</p>}
    </section>

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">AUTOMATIC SUGGESTIONS</p><h2>{detail.suggestions.length} suggestion(s)</h2><p>Using a suggestion only stages it in the editor. It becomes the selected metadata version only after the explicit save below.</p></div></div>
      {detail.suggestions.length ? <div className="card-grid">{detail.suggestions.map(suggestion => <SuggestionCard key={suggestion.id} suggestion={suggestion} generation={detail.generations.find(item => item.id === suggestion.generationId)?.status ?? "Queued"} staged={draft.sourceSuggestionId === suggestion.id} editable={editable} onUse={() => onDraftChange(draftFrom(suggestion))} />)}</div> : <p className="muted">The durable suggestion job has not produced an operator-safe suggestion yet.</p>}
    </section>

    <section className="split-layout metadata-editor-layout"><form className="panel stack" onSubmit={event => { event.preventDefault(); if (canSave) onSave(); }}><div className="section-heading"><div><p className="eyebrow">SELECTED METADATA REVISION</p><h2>{current ? `Revision ${current.number}` : "No selected revision"}</h2><p>Edits create a new immutable revision and invalidate any current preflight or approval.</p></div>{draft.sourceSuggestionId && <span className="status">Suggestion staged</span>}</div>
      <div className="form-grid"><label className="span-all">Title<input id="metadata-title" aria-label="Title" value={draft.title} maxLength={200} onChange={event => onDraftChange({ ...draft, title: event.target.value })} /><small>Maximum 100 Unicode characters.</small></label><label className="span-all">Description<textarea value={draft.description} maxLength={5000} onChange={event => onDraftChange({ ...draft, description: event.target.value })} /></label><label>Tags (comma separated)<input value={draft.tags} onChange={event => onDraftChange({ ...draft, tags: event.target.value })} placeholder="history, archive, analysis" /></label><label>Language<input value={draft.language} maxLength={64} onChange={event => onDraftChange({ ...draft, language: event.target.value })} placeholder="en" /></label><label>Category<select value={draft.categoryId} disabled={!catalogAvailable} onChange={event => onDraftChange({ ...draft, categoryId: event.target.value })}><option value="">Select a current YouTube category</option>{categoryOptions.map(category => <option disabled={!category.assignable} key={category.id} value={category.id}>{category.title}{category.assignable ? "" : " (no longer assignable)"}</option>)}</select></label><label>Playlist reference (optional)<input value={draft.playlistReference} maxLength={256} onChange={event => onDraftChange({ ...draft, playlistReference: event.target.value })} placeholder="Internal or remote opaque reference" /></label></div>
      <section className="metadata-private-note"><strong>Policy-controlled disclosure fields</strong><span>Made-for-kids and synthetic-media values are pinned from the active channel policy when this revision is saved.</span></section>
      <section className="metadata-private-note"><strong>Initial visibility: Private</strong><span>Visibility is fixed by the publishing policy in this workspace.</span></section>
      {editable && <div className="actions"><button type="submit" className="button-primary" disabled={!canSave || savePending}>{savePending ? "Saving revision…" : "Save selected metadata revision"}</button></div>}
      {!catalogAvailable && <p className="field-error">A fresh category catalog is required before a metadata revision can be selected.</p>}
    </form>
      <MetadataPreview version={current} draft={draft} />
    </section>

    {current && <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">PINNED EVIDENCE</p><h2>Exact media and policy inputs</h2><p>Every selected revision is tied to these immutable inputs before it can become publish-ready.</p></div></div><dl className="compact-definition"><dt>Final render</dt><dd>{shortId(current.renderRevisionId)}</dd><dt>Thumbnail selection</dt><dd>{shortId(current.thumbnailSelectionId)}</dd><dt>Caption revision</dt><dd>{shortId(current.captionRevisionId)}</dd><dt>Channel revision</dt><dd>{shortId(current.channelRevisionId)}</dd><dt>Metadata template</dt><dd>{shortId(current.metadataTemplateHash)}</dd><dt>Prompt template</dt><dd>{shortId(current.promptTemplateVersionId)}</dd><dt>Prompt snapshot</dt><dd>{shortId(current.promptContentHash)}</dd><dt>Made for kids</dt><dd>{String(current.isMadeForKids)}</dd><dt>Synthetic disclosure</dt><dd>{String(current.containsSyntheticMedia)}</dd>{current.relatedMainOutputId && <><dt>Short main-video reference</dt><dd>Internal output {shortId(current.relatedMainOutputId)}; no public URL is inferred before upload.</dd></>}</dl></section>}

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">METADATA PREFLIGHT</p><h2>{currentPreflight?.status ?? "Not run"}</h2><p>{preflightMessage(detail, currentPreflight?.status, catalogAvailable)}</p></div>{currentPreflight && <span className={`status status-${currentPreflight.status.toLowerCase()}`}>Current revision</span>}</div>
      {currentPreflight && <small>Input {shortId(currentPreflight.inputFingerprint)} · {timestamp(currentPreflight.completedAt ?? currentPreflight.createdAt)}</small>}
      {currentPreflightFindings.length ? <ol className="timeline">{currentPreflightFindings.map(finding => <li key={finding.id}><strong>{finding.severity} · {finding.code}</strong><span>{finding.summary ?? "Safe preflight finding"}</span>{finding.affectedField && <small>Field: {finding.affectedField}</small>}</li>)}</ol> : currentPreflight?.status === "Passed" ? <p className="muted">The current version has no recorded blocking metadata finding.</p> : null}
      {editable && <div className="actions"><button type="button" className="button-secondary" disabled={!canPreflight || preflightPending} onClick={onPreflight}>{preflightPending ? "Running preflight…" : "Run metadata preflight"}</button></div>}
    </section>

    <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">PUBLISH-READY GATE</p><h2>{detail.requiresHumanApproval ? "Explicit metadata approval required" : "Channel policy has no metadata approval stage"}</h2><p>{detail.requiresHumanApproval ? "A current preflight result still requires an editor or owner approval record." : "A current passing preflight advances the output under the channel policy."}</p></div>{gateStatus === "PublishReady" && <span className="status status-passed">PublishReady</span>}</div>
      {currentApproval && <p className="muted">{currentApproval.decision === "ApprovedByPolicy" ? "Policy approval" : "Metadata approval"} recorded {timestamp(currentApproval.createdAt)}: {currentApproval.reason}</p>}
      {detail.requiresHumanApproval && currentPreflightAllowsApproval && !currentApproval && editable && catalogAvailable && <fieldset><legend>Approve current metadata</legend><label>Approval reason<textarea required maxLength={1200} value={approvalReason} onChange={event => onApprovalReasonChange(event.target.value)} /></label><div className="actions"><button type="button" className="button-primary" disabled={!canApprove || approvalPending} onClick={onApprove}>{approvalPending ? "Recording approval…" : "Approve metadata"}</button></div></fieldset>}
      {detail.requiresHumanApproval && !currentPreflightAllowsApproval && !currentApproval && <p className="muted">Run a current passed metadata preflight before recording approval.</p>}
    </section>

    <MetadataHistory current={current} history={history} comparison={comparison} compareVersionId={compareVersionId} onCompareVersionChange={onCompareVersionChange} />
    {mutationMessage && <p className="field-error" role="alert">{mutationMessage}</p>}

    <ConfirmDialog open={saveConfirm} title="Save selected metadata revision?" message="This creates an immutable metadata revision from the staged values. Any prior metadata preflight or approval becomes stale; initial visibility remains Private." confirmLabel="Save metadata revision" onCancel={onSaveCancel} onConfirm={onSaveConfirm} />
    <ConfirmDialog open={refreshSuggestionsConfirm} title="Refresh categories and suggestions?" message="This queues a durable Worker cycle that may refresh the read-only YouTube category catalog and generate new proposals. It does not upload, change visibility, or mutate YouTube metadata." confirmLabel="Queue refresh" onCancel={onRefreshSuggestionsCancel} onConfirm={onRefreshSuggestionsConfirm} />
    <ConfirmDialog open={preflightConfirm} title="Run metadata preflight?" message="The current immutable metadata revision will be checked against YouTube limits, category availability, pinned media, attribution, and channel policy." confirmLabel="Run preflight" onCancel={onPreflightCancel} onConfirm={onPreflightConfirm} />
    <ConfirmDialog open={approvalConfirm} title="Approve metadata for publishing?" message="This records approval for the exact current metadata revision. It can make the output PublishReady; it does not dispatch an upload or change video visibility." confirmLabel="Approve metadata" onCancel={onApprovalCancel} onConfirm={onApprovalConfirm} />
  </section>;
}

function SuggestionCard({ suggestion, generation, staged, editable, onUse }: { suggestion: MetadataSuggestionView; generation: string; staged: boolean; editable: boolean; onUse(): void }): React.JSX.Element {
  return <article className="candidate-card metadata-suggestion"><div><strong>Suggestion {suggestion.candidateIndex}</strong><span className={`status status-${generation.toLowerCase()}`}>{generation}</span></div><div><h3>{suggestion.title}</h3><p>{suggestion.description}</p><small>{suggestion.tags.join(" · ") || "No tags suggested"}</small><div className="actions">{editable && <button type="button" className="button-secondary" onClick={onUse}>{staged ? "Staged in editor" : "Use in editor"}</button>}</div></div></article>;
}

function mergeCategories(catalog: MetadataCategoryView[], current: MetadataPackageVersionView | null): MetadataCategoryView[] {
  if (!current || catalog.some(category => category.id === current.categoryId)) return catalog;
  return [{ id: current.categoryId, title: current.categoryTitle, assignable: false }, ...catalog];
}

function MetadataPreview({ version, draft }: { version: MetadataPackageVersionView | null; draft: MetadataDraft }): React.JSX.Element {
  const thumbnailUrl = version?.thumbnailAssetId ? `/api/v1/assets/${version.thumbnailAssetId}/content` : null;
  const description = draft.description.length > 220 ? `${draft.description.slice(0, 217)}…` : draft.description;
  return <aside className="panel stack metadata-preview-panel"><div><p className="eyebrow">SEARCH + MOBILE PREVIEW</p><h2>Current editor values</h2><p className="muted">Preview only. It is not a remote YouTube listing.</p></div><div className="search-preview"><small>youtube.com</small><strong>{draft.title || "Untitled video"}</strong><p>{description || "Add a description to preview the search result."}</p></div><div className="mobile-preview">{thumbnailUrl ? <img className="thumbnail-mobile-preview" src={thumbnailUrl} alt="Pinned thumbnail preview" /> : <div className="thumbnail-mobile-placeholder">Pinned thumbnail pending</div>}<div><strong>{draft.title || "Untitled video"}</strong><small>Private · {draft.language || "language pending"}</small><small>{tagsFrom(draft.tags).join(" · ") || "No tags"}</small></div></div></aside>;
}

function MetadataHistory({ current, history, comparison, compareVersionId, onCompareVersionChange }: { current: MetadataPackageVersionView | null; history: MetadataPackageVersionView[]; comparison: MetadataPackageVersionView | null; compareVersionId: string | null; onCompareVersionChange(value: string): void }): React.JSX.Element | null {
  if (!current) return null;
  const changes = comparison ? metadataDiff(current, comparison) : [];
  return <section className="panel stack"><div className="section-heading"><div><p className="eyebrow">IMMUTABLE REVISION HISTORY</p><h2>Revision {current.number}</h2><p>Compare the selected revision with an earlier immutable version before repeating preflight or approval.</p></div></div><label>Compare against<select value={compareVersionId ?? comparison?.id ?? ""} onChange={event => onCompareVersionChange(event.target.value)}><option value="">No earlier version</option>{history.filter(version => version.id !== current.id).map(version => <option key={version.id} value={version.id}>Revision {version.number} · {timestamp(version.createdAt)}</option>)}</select></label>{comparison ? <div className="metadata-diff">{changes.length ? changes.map(change => <div key={change.label}><strong>{change.label}</strong><span><del>{change.before}</del><ins>{change.after}</ins></span></div>) : <p className="muted">The compared values are the same.</p>}</div> : <p className="muted">No earlier immutable metadata version is available.</p>}</section>;
}

function metadataDiff(current: MetadataPackageVersionView, previous: MetadataPackageVersionView): Array<{ label: string; before: string; after: string }> {
  const values: Array<[string, string, string]> = [
    ["Title", previous.title, current.title],
    ["Description", previous.description, current.description],
    ["Tags", previous.tags.join(", "), current.tags.join(", ")],
    ["Category", previous.categoryTitle, current.categoryTitle],
    ["Language", previous.language, current.language],
    ["Playlist reference", previous.playlistReference ?? "None", current.playlistReference ?? "None"],
    ["Thumbnail", shortId(previous.thumbnailAssetId), shortId(current.thumbnailAssetId)],
    ["Caption", shortId(previous.captionRevisionId), shortId(current.captionRevisionId)],
    ["Made for kids", String(previous.isMadeForKids), String(current.isMadeForKids)],
    ["Synthetic disclosure", String(previous.containsSyntheticMedia), String(current.containsSyntheticMedia)]
  ];
  return values.filter(([, before, after]) => before !== after).map(([label, before, after]) => ({ label, before, after }));
}

function preflightMessage(detail: MetadataPackageDetail, status: string | undefined, catalogAvailable: boolean): string {
  if (!catalogAvailable) return "The live category catalog is unavailable, so metadata preflight and approval fail closed.";
  if (!detail.currentVersion) return "Save an explicitly selected metadata revision before it can be preflighted.";
  if (!status) return "Run preflight against the exact selected revision before it can become publish-ready.";
  if (status === "Stale") return "A pinned media, policy, category, template, or metadata value changed. Run a current preflight again.";
  if (status === "Passed") return "The current metadata revision passed its required checks.";
  if (status === "Warning") return "A warning remains fail-closed. Resolve it with a new selected revision and a current passing preflight.";
  if (status === "Blocked") return "A required YouTube, rights, attribution, or channel-policy check blocked this metadata revision.";
  return "Preflight is still running or needs to be retried before the metadata can advance.";
}

function metadataGateStatus(detail: MetadataPackageDetail, preflightStatus: string | undefined, approvalDecision: string | undefined, catalogAvailable: boolean): string {
  if (!detail.currentVersion) return "Awaiting selection";
  if (!catalogAvailable) return "Category catalog unavailable";
  if (approvalDecision === "Approved" || approvalDecision === "ApprovedByPolicy") return "PublishReady";
  if (preflightStatus === "Passed" && !detail.requiresHumanApproval) return "PublishReady";
  if (preflightStatus === "Passed") return "Awaiting approval";
  if (preflightStatus === "Warning") return "Preflight warning";
  if (preflightStatus === "Blocked") return "Preflight blocked";
  return "Preflight required";
}
